import React, {useEffect, useState} from "react";
import {Box, Stack, Tooltip, Typography} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {CallIcon, CallOutgoing01Icon, CheckmarkCircle02Icon, Copy01Icon, Message02Icon} from "@hugeicons/core-free-icons";
import {toast} from "react-toastify";

// Pieces of a conversation row, shared by the conversations table and the conversation modal

const SENDER_ID_MAX_LENGTH = 18;

// The caller shown for a conversation. A contact name is shown as is; a raw sender id is cut to 10 characters
// (full id on hover) with a copy button next to it.
// sender: the conversation's sender id, to tell whether `name` is the raw id.
export const SenderName = ({name, sender, sx}) => {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const timeoutId = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(timeoutId);
  }, [copied]);

  if (!name) return <Typography noWrap sx={sx}>Unknown</Typography>;
  const text = String(name);
  if (text !== String(sender ?? "")) return <Typography noWrap sx={sx}>{text}</Typography>;

  const copy = (event) => {
    event.stopPropagation();
    navigator.clipboard.writeText(text)
      .then(() => setCopied(true))
      .catch(() => toast.error("Failed to copy"));
  };
  return (
    <Stack direction={"row"} alignItems={"center"} gap={0.25} sx={{minWidth: 0}}>
      <Tooltip title={text}>
        <Typography noWrap sx={sx}>
          {text.length > SENDER_ID_MAX_LENGTH ? `${text.slice(0, SENDER_ID_MAX_LENGTH)}…` : text}
        </Typography>
      </Tooltip>
      <Tooltip title={"Copy"}>
        <Box component={"span"} role={"button"} aria-label={"Copy sender id"} onClick={copy}
             onKeyDown={(e) => e.key === "Enter" && copy(e)} tabIndex={0}
             sx={{
               display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0, width: 22,
               height: 22, borderRadius: "50%", color: copied ? "#1E8A55" : "#8A8AA0", cursor: "pointer",
               "&:hover": {bgcolor: "#ECECF3"},
               "&:hover > div": {display: "flex"},
             }}>
          <Box sx={{display: "none"}}>
            <HugeiconsIcon icon={copied ? CheckmarkCircle02Icon : Copy01Icon} size={13}/>
          </Box>
        </Box>
      </Tooltip>
    </Stack>
  );
};

export const STATUS_STYLES = {
  ACTIVE: {bg: "#EEF0FE", color: "#4B55C9"},
  FINISHED: {bg: "#E8F7EF", color: "#1E8A55"},
  SUCCESS: {bg: "#E8F7EF", color: "#1E8A55"},
  REDIRECTED: {bg: "#F1EEFB", color: "#6E5BC4"},
  HANDOVER: {bg: "#F1EEFB", color: "#6E5BC4"},
  AGENT_CALL_INITIATED: {bg: "#F1EEFB", color: "#6E5BC4"},
  HANGUP: {bg: "#FDF4E3", color: "#A86A0C"},
  USER_NOT_TALKING: {bg: "#FDF4E3", color: "#A86A0C"},
  MISSED_CALL: {bg: "#FDECEC", color: "#C73A3A"},
  FAILED: {bg: "#FDECEC", color: "#C73A3A"},
};

export const AVATAR_COLORS = [
  {bg: "#EEF0FE", color: "#4B55C9"},
  {bg: "#E7F1F8", color: "#3D7AA6"},
  {bg: "#F1EEFB", color: "#6E5BC4"},
  {bg: "#E8F7EF", color: "#1E8A55"},
  {bg: "#FDF4E3", color: "#A86A0C"},
];

export const formatStatus = (status) => status
  ? status.toLowerCase().replaceAll("_", " ").replace(/^\w/, c => c.toUpperCase())
  : "—";

export const isAnnotated = (conversation) => conversation?.annotated === true;

// Initials of a name; phone numbers / ids get no initials (an icon is shown instead)
export const initialsOf = (name) => {
  if (!name || !/[a-zA-Z]/.test(name)) return null;
  return name.trim().split(/\s+/).slice(0, 2).map(part => part[0]).join("").toUpperCase();
};

// Live (ACTIVE) conversations: brand-filled avatar with a ringing icon and a pulsing ring, tinted table row with an
// accent bar. Animations stop for users who prefer reduced motion.
const LIVE_COLOR = "#4B55C9";
const reducedMotion = {"@media (prefers-reduced-motion: reduce)": {animation: "none"}};

export const LIVE_ROW_SX = {
  bgcolor: "#F7F8FF",
  "& td": {borderBottomColor: "#ECEEFD"},
  "& td:first-of-type": {boxShadow: `inset 3px 0 0 ${LIVE_COLOR}`},
  "&.MuiTableRow-hover:hover": {bgcolor: "#F0F2FE !important"},
};

export const LiveAvatar = ({isVoice = true, size = 28, iconSize = 14}) => (
  <Box sx={{
    position: "relative", width: size, height: size, borderRadius: "50%", flexShrink: 0, display: "flex",
    alignItems: "center", justifyContent: "center", bgcolor: LIVE_COLOR + 33, color: LIVE_COLOR,
    boxShadow: "0 0 0 0 rgba(75, 85, 201, 0.45)",
    animation: "live-avatar-pulse 1.8s infinite",
    "@keyframes live-avatar-pulse": {
      "0%": {boxShadow: "0 0 0 0 rgba(75, 85, 201, 0.45)"},
      "70%": {boxShadow: "0 0 0 7px rgba(75, 85, 201, 0)"},
      "100%": {boxShadow: "0 0 0 0 rgba(75, 85, 201, 0)"},
    },
    ...reducedMotion,
  }}>
    <Box component={"span"} sx={{
      display: "flex",
      // a short buzz, then a pause - like a phone vibrating
      animation: isVoice ? "live-avatar-ring 1.6s ease-in-out infinite" : "none",
      "@keyframes live-avatar-ring": {
        "0%, 50%, 100%": {transform: "rotate(0deg)"},
        "5%, 15%, 25%, 35%": {transform: "rotate(-14deg)"},
        "10%, 20%, 30%, 40%": {transform: "rotate(14deg)"},
      },
      ...reducedMotion,
    }}>
      <HugeiconsIcon icon={isVoice ? CallIcon : Message02Icon} size={iconSize}/>
    </Box>
  </Box>
);

export const StatusBadge = ({conversation}) => {
  const style = STATUS_STYLES[conversation?.status] || {bg: "#F2F2F7", color: "#6B6B80"};
  const isLive = conversation?.status === "ACTIVE";
  const target = conversation?.vdn || conversation?.transferNumber;
  return (
    <Tooltip title={target ? `To ${target}` : ""}>
      <Box component={"span"} sx={{
        display: "inline-flex", alignItems: "center", gap: 0.75, px: 1, py: 0.25, borderRadius: "999px",
        fontSize: 12, fontWeight: isLive ? 600 : 500, whiteSpace: "nowrap", bgcolor: style.bg, color: style.color,
        ...(isLive ? {boxShadow: "inset 0 0 0 1px rgba(75, 85, 201, 0.25)"} : {}),
      }}>
        <Box component={"span"} sx={{
          width: 6, height: 6, borderRadius: "50%", bgcolor: "currentColor",
          ...(isLive ? {
            animation: "status-live-blink 1.4s ease-in-out infinite",
            "@keyframes status-live-blink": {"0%, 100%": {opacity: 1}, "50%": {opacity: 0.25}},
            ...reducedMotion,
          } : {}),
        }}/>
        {formatStatus(conversation?.status)}
      </Box>
    </Tooltip>
  );
};

// Number the conversation was transferred to (redirected / handed over), shown after the caller; nothing when
// it was not transferred
export const TransferNumber = ({conversation, size = "small"}) => {
  const target = conversation?.transferNumber || conversation?.vdn;
  if (!target) return null;
  return (
    <Tooltip title={`Transferred to ${target}`}>
      <Box component={"span"} sx={{
        display: "inline-flex", alignItems: "center", gap: 0.5, flexShrink: 0, px: 0.75, py: 0.125,
        borderRadius: "6px", fontSize: size === "small" ? 11 : 12, fontWeight: 600, whiteSpace: "nowrap",
        fontVariantNumeric: "tabular-nums", color: "#6E5BC4", bgcolor: "#F1EEFB",
      }}>
        <HugeiconsIcon icon={CallOutgoing01Icon} size={size === "small" ? 12 : 13}/>
        {target}
      </Box>
    </Tooltip>
  );
};

export const TagPills = ({names}) => {
  if (!names.length) return <Typography sx={{fontSize: 10, color: "#B4B4C3"}}>—</Typography>;
  const [first, ...rest] = names;
  return (
    <Tooltip title={names.join(", ")}>
      <Stack direction={"row"} gap={0.5} alignItems={"center"} sx={{minWidth: 0}}>
        <Box component={"span"} sx={{
          px: 1, py: 0.25, borderRadius: "6px", fontSize: 11, color: "#4A4A63", bgcolor: "#F2F2F7",
          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0,
        }}>
          {first}
        </Box>
        {rest.length > 0 && <Typography sx={{fontSize: 10, color: "#8A8AA0"}}>+{rest.length}</Typography>}
      </Stack>
    </Tooltip>
  );
};
